// components/formDialog/PlacementDialog.tsx
import AsyncMultiSelectField from "@/components/form/components/AsyncMultiSelectField";
import AsyncSingleSelectField from "@/components/form/components/AsyncSingleSelectField";
import InputField from "@/components/form/components/InputField";
import SelectField from "@/components/form/components/SelectField";
import TextareaField from "@/components/form/components/Textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreatePlacementMutation,
  useUpdatePlacementMutation,
} from "@/hooks/api/placement.queries";
import { batchService } from "@/services/batch.service";
import { departmentService } from "@/services/department.service";
import {
  PlacementCreateType,
  PlacementParamsType,
  PlacementRecordType,
} from "@/services/placement.service";
import { placementVendorService } from "@/services/placementVendor.service";
import { studentService } from "@/services/student.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

type PlacementDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: PlacementParamsType;
  placementId?: string;
  placementData?: PlacementRecordType | null;
  onCancel: () => void;
};

const placementSchema = z.object({
  role: z.string().min(1, "Job Role is required"),
  company: z.string().min(1, "Company is required"),
  students: z.array(z.string()).optional(),
  departments: z.array(z.string()).optional(),
  batches: z.array(z.string()).optional(),
  package: z.number().min(0, "Package must be a valid number"),
  jobType: z.enum(["Full-time", "Part-time", "Contract", "Internship"]),
  placementDate: z.string().min(1, "Placement Date is required"),
  joiningDate: z.string().optional(),
  status: z.enum(["confirmed", "offer_pending", "rejected"]),
  eligibilityCriteria: z.string().optional(),
  jobDescription: z.string().optional(),
  requirements: z.string().optional(),
});

type PlacementFormType = z.infer<typeof placementSchema>;

const JOB_TYPE_OPTIONS = [
  { label: "Full-time", value: "Full-time" },
  { label: "Part-time", value: "Part-time" },
  { label: "Contract", value: "Contract" },
  { label: "Internship", value: "Internship" },
];

const STATUS_OPTIONS = [
  { label: "Offer Pending", value: "offer_pending" },
  { label: "Confirmed", value: "confirmed" },
  { label: "Rejected", value: "rejected" },
];

const PlacementDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  placementId,
  placementData,
  onCancel,
}: PlacementDialogProps) => {
  const createMutation = useCreatePlacementMutation(filters);
  const updateMutation = useUpdatePlacementMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<PlacementFormType>({
    resolver: zodResolver(placementSchema),
    defaultValues: {
      role: "",
      company: "",
      students: [],
      departments: [],
      batches: [],
      package: 0,
      jobType: "Full-time",
      placementDate: "",
      joiningDate: "",
      status: "offer_pending",
      eligibilityCriteria: "",
      jobDescription: "",
      requirements: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && placementData) {
        setTimeout(() => {
          form.reset({
            role: placementData.role,
            company: placementData.company,
            students: placementData.students || [],
            departments: placementData.departments || [],
            batches: placementData.batches || [],
            package: placementData.package,
            jobType: placementData.jobType,
            placementDate: placementData.placementDate,
            joiningDate: placementData.joiningDate || "",
            status: placementData.status,
            eligibilityCriteria: placementData.eligibilityCriteria || "",
            jobDescription: placementData.jobDescription || "",
            requirements: placementData.requirements || "",
          });
        }, 0);
      } else {
        form.reset({
          role: "",
          company: "",
          students: [],
          departments: [],
          batches: [],
          package: 0,
          jobType: "Full-time",
          placementDate: "",
          joiningDate: "",
          status: "offer_pending",
          eligibilityCriteria: "",
          jobDescription: "",
          requirements: "",
        });
      }
    }
  }, [mode, placementData, isOpen, form]);

  const handleSubmit = (values: PlacementFormType) => {
    const submissionData: PlacementCreateType = {
      ...values,
      students: values.students || [],
      departments: values.departments || [],
      batches: values.batches || [],
      joiningDate: values.joiningDate || "",
      eligibilityCriteria: values.eligibilityCriteria || "",
      jobDescription: values.jobDescription || "",
      requirements: values.requirements || "",
    };

    if (mode === "create") {
      createMutation.mutate(submissionData, { onSuccess: onCancel });
    } else if (mode === "edit" && placementId) {
      updateMutation.mutate(
        { _id: placementId, data: submissionData },
        { onSuccess: onCancel }
      );
    }
  };

  // Convert IDs for default options mapping to prevent TypeScript errors.
  const defaultCompanyOptions = placementData?.company
    ? [
        {
          label: `Company ${placementData.company}`, // Fallback if data not joined
          value: placementData.company,
        },
      ]
    : [];

  const defaultStudentOptions =
    placementData?.students?.map((id) => ({
      label: `Student ${id}`,
      value: id,
    })) || [];

  const defaultDepartmentOptions =
    placementData?.departments?.map((id) => ({
      label: `Dept ${id}`,
      value: id,
    })) || [];

  const defaultBatchOptions =
    placementData?.batches?.map((id) => ({
      label: `Batch ${id}`,
      value: id,
    })) || [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add New Placement" : "Edit Placement"}
          </DialogTitle>
          <p className="text-muted-foreground mt-1 text-sm">
            Fill in the details to{" "}
            {mode === "create" ? "add a new" : "edit the"} placement
          </p>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2"
          >
            {/* Job Role */}
            <InputField<PlacementFormType>
              type="text"
              name="role"
              label="Job Role"
              placeholder="e.g., Software Engineer"
              required
              disabled={isPending}
            />

            {/* Company/Vendor */}
            <AsyncSingleSelectField<PlacementFormType>
              name="company"
              label="Company/Vendor"
              placeholder="Select company"
              fetchOptions={placementVendorService.fetchPlacementVendorOptions}
              queryKey={["placementVendors"]}
              defaultOptions={defaultCompanyOptions}
              required
              disabled={isPending}
            />

            {/* Select Students */}
            <div className="md:col-span-2">
              <AsyncMultiSelectField<PlacementFormType>
                name="students"
                label="Select Students"
                placeholder="Select students for this placement"
                fetchOptions={studentService.fetchStudentOptions}
                queryKey={["students"]}
                defaultOptions={defaultStudentOptions}
                disabled={isPending}
              />
            </div>

            {/* Departments */}
            <AsyncMultiSelectField<PlacementFormType>
              name="departments"
              label="Departments"
              placeholder="Select departments"
              fetchOptions={departmentService.fetchDepartmentOptions}
              queryKey={["departments"]}
              defaultOptions={defaultDepartmentOptions}
              disabled={isPending}
            />

            {/* Batches */}
            <AsyncMultiSelectField<PlacementFormType>
              name="batches"
              label="Batches"
              placeholder="Select batches"
              fetchOptions={batchService.fetchBatchOptions}
              queryKey={["batches"]}
              defaultOptions={defaultBatchOptions}
              disabled={isPending}
            />

            {/* Salary Package */}
            <InputField<PlacementFormType>
              type="number"
              name="package"
              label="Salary Package (LPA)"
              placeholder="e.g., 6"
              required
              disabled={isPending}
            />

            {/* Job Type */}
            <SelectField<PlacementFormType>
              name="jobType"
              label="Job Type"
              options={JOB_TYPE_OPTIONS}
              required
              disabled={isPending}
            />

            {/* 3-column Layout for Dates and Status */}
            <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-3">
              {/* Placement Date */}
              <InputField<PlacementFormType>
                type="date"
                name="placementDate"
                label="Placement Date"
                required
                disabled={isPending}
              />

              {/* Joining Date */}
              <InputField<PlacementFormType>
                type="date"
                name="joiningDate"
                label="Joining Date"
                disabled={isPending}
              />

              {/* Status */}
              <SelectField<PlacementFormType>
                name="status"
                label="Status"
                options={STATUS_OPTIONS}
                required
                disabled={isPending}
              />
            </div>

            {/* Eligibility Criteria */}
            <TextareaField<PlacementFormType>
              name="eligibilityCriteria"
              label="Eligibility Criteria"
              placeholder="e.g., Minimum 70% marks, No active backlogs"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Job Description */}
            <TextareaField<PlacementFormType>
              name="jobDescription"
              label="Job Description"
              placeholder="Brief description about the job role"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Requirements */}
            <TextareaField<PlacementFormType>
              name="requirements"
              label="Requirements"
              placeholder="Skills, qualifications, or other requirements"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            <DialogFooter className="md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "create"
                    ? "Adding..."
                    : "Saving..."
                  : mode === "create"
                    ? "Create Placement"
                    : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PlacementDialog;
